import { type AcpSessionConfigOption, acpAgentCatalogEntry } from '@crowdex/agent-contracts';
import type { NativeAgent } from '@/lib/native-desktop';

export type AcpSelectConfigOption = Extract<AcpSessionConfigOption, { type: 'select' }>;
export type ConfigChoice = { value: string; name: string; description?: string };

/**
 * Why a model option has nothing to choose from: its models are loading, could not be read (a
 * failure, a timeout, an older CLI: Retry asks again), or the agent answered with none.
 */
export type ModelListState =
  | { state: 'loading'; message?: string }
  | { state: 'failed'; message: string }
  | { state: 'empty'; message: string };

const MODEL_LIST_META_KEY = 'crowdexModelList';

/** The value the model popover's Retry sends for the model option: ask the agent again. */
export const MODEL_LIST_RETRY = 'crowdex:retry-model-list';

/** The `_meta` of a model option with nothing to choose from, saying why. */
export function modelListMeta(state: ModelListState): Record<string, unknown> {
  return { [MODEL_LIST_META_KEY]: state };
}

/** Why the model option has nothing to choose from, when it says so. */
export function modelListState(option: AcpSelectConfigOption): ModelListState | null {
  const value = option._meta?.[MODEL_LIST_META_KEY];
  if (value === null || typeof value !== 'object') return null;
  const state = (value as { state?: unknown }).state;
  const message = (value as { message?: unknown }).message;
  if (state === 'loading') return { state };
  if ((state === 'failed' || state === 'empty') && typeof message === 'string')
    return { state, message };
  return null;
}

/** An agent's own name in a reply's `modelId`: it says nothing of the model. */
const MODEL_PLACEHOLDERS = new Set(['acp', 'claude-code', 'codex', 'opencode']);

/** A model id as compared: `claude-opus-5[1m]` and its replies' `claude-opus-5` are one model. */
function comparableModelId(id: string): string {
  return id
    .trim()
    .toLowerCase()
    .replace(/\[[^\]]*\]$/, '');
}

/**
 * The agent's concrete models: its "default" pseudo-entry is not one (it stands for whatever the
 * agent's configuration says, which never decides here).
 */
export function concreteModelChoices(option: AcpSelectConfigOption | undefined): ConfigChoice[] {
  if (option === undefined) return [];
  return flattenConfigOptions(option).filter((choice) => {
    const value = choice.value.trim().toLowerCase();
    return value.length > 0 && value !== 'default';
  });
}

/** The listed model an id stands for: the same value, else the same model without its suffix. */
export function matchModelChoice(
  id: string | null | undefined,
  choices: readonly ConfigChoice[],
): ConfigChoice | null {
  const wanted = id?.trim();
  if (!wanted || MODEL_PLACEHOLDERS.has(wanted.toLowerCase())) return null;
  return (
    choices.find((choice) => choice.value === wanted) ??
    choices.find((choice) => comparableModelId(choice.value) === comparableModelId(wanted)) ??
    null
  );
}

/**
 * The model a conversation runs, always a concrete one: the one picked (in the conversation, or
 * saved with it), else the one its last turn ran, else the user's last pick for the agent, else
 * the first the agent lists. Without a list, the first of those that names a model; '' when none.
 */
export function selectModel(
  candidates: readonly (string | null | undefined)[],
  choices: readonly ConfigChoice[],
): string {
  if (choices.length === 0) {
    const named = candidates.find(
      (candidate) => candidate?.trim() && !MODEL_PLACEHOLDERS.has(candidate.trim().toLowerCase()),
    );
    return named?.trim() ?? '';
  }
  for (const candidate of candidates) {
    const match = matchModelChoice(candidate, choices);
    if (match !== null) return match.value;
  }
  return choices[0]?.value ?? '';
}

/** How a choice reads to people: agents report efforts as ids ("medium", "xhigh"). */
const EFFORT_NAMES: Readonly<Record<string, string>> = {
  minimal: 'Minimal',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  xhigh: 'Extra high',
  max: 'Max',
};

export function displayChoiceName(name: string): string {
  const known = EFFORT_NAMES[name.trim().toLowerCase()];
  if (known) return known;
  // A single lowercase word (an effort id) reads capitalised; ids with digits or dashes stay as is.
  return /^[a-z]+$/.test(name) ? name[0]?.toUpperCase() + name.slice(1) : name;
}

/** Known agents expose convenient effort defaults; ACP options replace them after startup. */
export function nativeAgentConfigOptions(
  agent: NativeAgent | null,
  values: Readonly<Record<string, string | boolean>>,
): readonly AcpSessionConfigOption[] {
  if (agent === null) return [];
  const config = acpAgentCatalogEntry(agent)?.sessionConfig.find(
    (entry) => entry.choices && entry.aliases.includes('reasoningEffort'),
  );
  const efforts = config?.choices;
  if (!efforts?.length) return [];
  const configured =
    values['thought_level'] ??
    values['effort'] ??
    values['reasoning_effort'] ??
    values['reasoningEffort'];
  const currentValue =
    typeof configured === 'string' && efforts.some((effort) => effort === configured)
      ? configured
      : 'medium';
  return [
    {
      id: 'thought_level',
      name: 'Effort',
      category: 'thought_level',
      type: 'select',
      currentValue,
      options: efforts.map((effort) => ({ value: effort, name: effort })),
    },
  ];
}

export function currentModelOption(
  options: readonly AcpSelectConfigOption[],
): AcpSelectConfigOption | undefined {
  return options.find((option) => option.category === 'model');
}

export function currentModelName(options: readonly AcpSelectConfigOption[]): string | null {
  const model = currentModelOption(options);
  if (model === undefined) return null;
  return (
    flattenConfigOptions(model).find((choice) => choice.value === model.currentValue)?.name ??
    model.name
  );
}

export function flattenConfigOptions(option: AcpSelectConfigOption): ConfigChoice[] {
  return option.options
    .flatMap((entry) => ('options' in entry ? entry.options : [entry]))
    .map((choice) => ({
      value: choice.value,
      name: choice.name,
      description: choice.description ?? undefined,
    }));
}
